import React, { useEffect, useState } from 'react';
import { IndianRupee, ShoppingCart, Package, Users, TrendingUp, AlertTriangle, MessageSquareText, Clock, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import Loader from '@/components/common/Loader';

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/admin/analytics').then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loader />;
  const kpis = [
    ['Revenue', inr(d.revenue), IndianRupee],
    ['Orders', d.total_orders, ShoppingCart],
    ['Products', d.total_products, Package],
    ['Customers', d.total_customers, Users],
    ['Avg Order Value', inr(d.aov), TrendingUp],
    ['Pending Orders', d.pending_orders ?? 0, Clock],
    ['New Inquiries', d.pending_inquiries ?? 0, MessageSquareText],
  ];
  return (
    <div>
      <h1 className="text-2xl font-semibold">Dashboard</h1>
      <p className="text-sm text-muted-foreground">Welcome back to Gaze Decor</p>
      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        {kpis.map(([label, val, Icon]) => (
          <div key={label} className="rounded-2xl border border-border bg-white p-4">
            <div className="flex items-center justify-between"><span className="text-[10px] uppercase text-muted-foreground">{label}</span><Icon size={15} className="gaze-gold" /></div>
            <p className="mt-2 text-xl font-bold">{val}</p>
            <div className="mt-2 h-px w-6 bg-[hsl(var(--gaze-gold))]/60" />
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-white p-5 lg:col-span-2">
          <h2 className="mb-4 text-sm font-semibold">Revenue (last 7 days)</h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.revenue_by_day}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(158 60% 15%)" stopOpacity={0.3} /><stop offset="95%" stopColor="hsl(158 60% 15%)" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => inr(v)} />
              <Area type="monotone" dataKey="revenue" stroke="hsl(158 60% 15%)" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><AlertTriangle size={15} className="text-amber-500" /> Low Stock</h2>
          <div className="space-y-2">
            {(d.low_stock || []).length === 0 ? <p className="text-sm text-muted-foreground">All well stocked.</p> : d.low_stock.slice(0, 8).map((p) => (
              <div key={p.sku} className="flex items-center justify-between text-sm"><span className="truncate pr-2">{p.name}</span><span className={p.stock === 0 ? 'font-semibold text-destructive' : 'font-medium text-amber-600'}>{p.stock}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-white p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Recent Orders</h2>
            <Link to="/admin/orders" className="text-xs font-semibold text-[hsl(var(--primary))]">View all →</Link>
          </div>
          <div className="space-y-2">
            {(d.recent_orders || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0"><span className="font-medium">{o.order_number}</span><span className="text-muted-foreground">{o.address?.full_name}</span><span className="capitalize">{o.status}</span><span className="font-semibold">{inr(o.total)}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-semibold"><MessageSquareText size={15} className="text-[hsl(var(--primary))]" /> Recent Inquiries</h2>
            <Link to="/admin/inquiries" className="text-xs font-semibold text-[hsl(var(--primary))]">View all →</Link>
          </div>
          <div className="space-y-2">
            {(d.recent_inquiries || []).length === 0 ? <p className="text-sm text-muted-foreground">No inquiries yet.</p> : (d.recent_inquiries || []).map((inq) => (
              <div key={inq.id} className="border-b border-border py-2 text-xs last:border-0">
                <div className="flex items-center justify-between"><span className="font-semibold capitalize">{inq.inquiry_type}</span><span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${inq.status === 'new' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{inq.status}</span></div>
                <p className="mt-0.5 truncate text-muted-foreground">{inq.product_name || 'General'} · {inq.name || inq.email || inq.phone || 'Guest'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-white p-5">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Star size={15} className="text-[hsl(var(--gaze-gold))]" /> Best Sellers</h2>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {(d.best_sellers || []).map((p) => (
            <div key={p.sku} className="flex items-center gap-2 rounded-xl border border-border p-2">
              <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />
              <div className="min-w-0 flex-1"><p className="truncate text-xs font-semibold">{p.name}</p><p className="text-[10px] text-muted-foreground">{p.sold} sold · {inr(p.price)}</p></div>
            </div>
          ))}
          {(d.best_sellers || []).length === 0 && <p className="text-sm text-muted-foreground">No sales data yet.</p>}
        </div>
      </div>
    </div>
  );
}
